// src/modules/upgrade-application/components/steps/FellowshipStep.tsx
// Fellow-only upgrade step. Collects the three sections the official
// "Application for Membership Upgrade (Fellow)" form asks for and the
// Probationer → Member upgrade does NOT:
//   14. Papers presented at seminars/workshops or published since election
//   15. Dissertation (>= 5,000 words) — required ONLY when no paper is listed
//   16. Contributions to NIQS benevolent activities since election
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Award,
  Trash2,
  ArrowLeft,
  ArrowRight,
  Type,
  Calendar,
  MapPin,
  PlusCircle,
  FileText,
  UploadCloud,
  HeartHandshake,
  Tag,
} from "lucide-react";
import { BENEVOLENCE_TYPES, getYears } from "../../constants";

export type PaperRow = { title: string; presented_at?: string; year?: string };
export type BenevolenceRow = { description: string; type?: string; year?: string };

export type FellowshipInitial = {
  papers: PaperRow[];
  benevolences: BenevolenceRow[];
  dissertation_topic?: string;
  dissertation_file?: string; // existing stored path (preserved on edit)
  dissertation_url?: string; // absolute URL for the existing file link
};

export type FellowshipPayload = {
  papers: PaperRow[];
  benevolences: BenevolenceRow[];
  dissertation_topic: string;
  dissertationFile: File | null;
  dissertation_file_path: string; // existing path echoed back so it isn't wiped
};

export default function FellowshipStep({
  onSubmit,
  busy,
  onBack,
  initialValues,
  readOnly,
}: {
  busy: boolean;
  onBack: () => void;
  readOnly?: boolean;
  initialValues?: FellowshipInitial | null;
  onSubmit: (payload: FellowshipPayload) => Promise<void>;
}) {
  const emptyPaper: PaperRow = useMemo(() => ({ title: "", presented_at: "", year: "" }), []);
  const emptyBen: BenevolenceRow = useMemo(() => ({ description: "", type: "", year: "" }), []);

  const [papers, setPapers] = useState<PaperRow[]>([{ ...emptyPaper }]);
  const [benevolences, setBenevolences] = useState<BenevolenceRow[]>([{ ...emptyBen }]);
  const [dissertationTopic, setDissertationTopic] = useState("");
  const [dissertationFile, setDissertationFile] = useState<File | null>(null);
  const [existingPath, setExistingPath] = useState("");
  const [existingUrl, setExistingUrl] = useState("");
  const [error, setError] = useState<string | null>(null);

  const years = useMemo(() => getYears(1970), []);
  const hydratedRef = useRef(false);

  useEffect(() => {
    if (!initialValues || hydratedRef.current) return;
    hydratedRef.current = true;

    const p = initialValues.papers || [];
    if (p.length) setPapers(p.map((x) => ({ title: x.title ?? "", presented_at: x.presented_at ?? "", year: String(x.year ?? "") })));

    const b = initialValues.benevolences || [];
    if (b.length) setBenevolences(b.map((x) => ({ description: x.description ?? "", type: x.type ?? "", year: String(x.year ?? "") })));

    setDissertationTopic(initialValues.dissertation_topic ?? "");
    setExistingPath(initialValues.dissertation_file ?? "");
    setExistingUrl(initialValues.dissertation_url ?? "");
  }, [initialValues]);

  const setPaper = (i: number, k: keyof PaperRow, v: string) =>
    setPapers((s) => s.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)));
  const setBen = (i: number, k: keyof BenevolenceRow, v: string) =>
    setBenevolences((s) => s.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)));

  const cleanPapers = () => papers.filter((p) => (p.title ?? "").trim() !== "");
  const cleanBenevolences = () => benevolences.filter((b) => (b.description ?? "").trim() !== "");

  const hasDissertation = Boolean(dissertationFile) || existingPath.trim() !== "";

  const submit = async () => {
    setError(null);
    const papersOut = cleanPapers();

    // Item 15 rule: at least one paper OR a dissertation must be provided.
    if (papersOut.length === 0 && !hasDissertation) {
      setError(
        "List at least one paper (item 14), or upload a dissertation of at least 5,000 words (item 15)."
      );
      return;
    }

    await onSubmit({
      papers: papersOut,
      benevolences: cleanBenevolences(),
      dissertation_topic: dissertationTopic.trim(),
      dissertationFile,
      dissertation_file_path: existingPath,
    });
  };

  return (
    <form
      className="space-y-8 pb-10"
      onSubmit={async (e) => {
        e.preventDefault();
        await submit();
      }}
    >
      {/* Header */}
      <div className="border-b border-gray-100 pb-5">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-gray-900 rounded-xl">
            <Award className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900">Fellowship Details</h2>
            <p className="text-sm text-gray-500">
              Required for the Fellow grade: your papers, dissertation, and contributions to the
              Institute since your election as a Member.
            </p>
          </div>
        </div>
      </div>

      {error && (
        <div className="p-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>
      )}

      {/* ── Item 14 — Papers ─────────────────────────────────────────────── */}
      <section className="space-y-4">
        <div className="flex items-center gap-2">
          <FileText className="w-4 h-4 text-gray-700" />
          <h3 className="text-sm font-bold text-gray-900 uppercase tracking-tight">
            14 · Papers presented / published since election
          </h3>
        </div>
        <div className="space-y-4">
          {papers.map((r, i) => (
            <div key={i} className="group relative bg-white border border-gray-200 rounded-2xl p-6 hover:border-gray-300 hover:shadow-sm transition-all">
              {papers.length > 1 && (
                <button type="button" className="absolute top-4 right-4 p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all"
                  onClick={() => setPapers((x) => x.filter((_, idx) => idx !== i))}>
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
              <div className="grid md:grid-cols-3 gap-6">
                <Field label="Paper Title" icon={<Type className="w-3 h-3" />} value={r.title}
                  placeholder="e.g. Cost management in public projects" onChange={(v) => setPaper(i, "title", v)} />
                <Field label="Presented / Published at" icon={<MapPin className="w-3 h-3" />} value={r.presented_at || ""}
                  placeholder="e.g. NIQS Annual Conference / NIQS Journal" onChange={(v) => setPaper(i, "presented_at", v)} />
                <SelectField label="Year" icon={<Calendar className="w-3 h-3" />} value={r.year || ""} options={years}
                  onChange={(v) => setPaper(i, "year", v)} />
              </div>
            </div>
          ))}
        </div>
        <button type="button" onClick={() => setPapers((s) => [...s, { ...emptyPaper }])}
          className="flex items-center gap-2 px-5 py-2.5 text-gray-900 border-2 border-dashed border-gray-300 rounded-2xl text-xs font-bold hover:border-gray-900 transition-all">
          <PlusCircle className="w-4 h-4" /> Add Paper
        </button>
      </section>

      {/* ── Item 15 — Dissertation ───────────────────────────────────────── */}
      <section className="space-y-4">
        <div className="flex items-center gap-2">
          <UploadCloud className="w-4 h-4 text-gray-700" />
          <h3 className="text-sm font-bold text-gray-900 uppercase tracking-tight">15 · Dissertation</h3>
        </div>
        <p className="text-xs text-gray-500 -mt-1">
          Required only if you have not listed any paper above. Must be at least 5,000 words on a topic
          relevant to quantity surveying, capable of being published in the NIQS Journal.
        </p>
        <div className="bg-white border border-gray-200 rounded-2xl p-6 space-y-5">
          <Field label="Dissertation Topic" icon={<Type className="w-3 h-3" />} value={dissertationTopic}
            placeholder="e.g. Life-cycle costing in Nigerian public infrastructure" onChange={setDissertationTopic} />
          <div className="space-y-1.5">
            <label className="text-xs font-bold text-gray-700 uppercase flex items-center gap-2 tracking-tight">
              <FileText className="w-3 h-3" /> Dissertation File (PDF / Word)
            </label>
            <input type="file" accept=".pdf,.doc,.docx"
              onChange={(e) => setDissertationFile(e.target.files?.[0] ?? null)}
              className="w-full text-sm text-gray-600 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-xs file:font-bold file:bg-gray-900 file:text-white hover:file:bg-gray-800" />
            {dissertationFile ? (
              <p className="text-xs text-green-600 font-medium">Selected: {dissertationFile.name}</p>
            ) : existingPath ? (
              <p className="text-xs text-gray-500">
                Current file kept.{" "}
                {existingUrl && (
                  <a href={existingUrl} target="_blank" rel="noreferrer" className="text-indigo-600 underline">
                    View
                  </a>
                )}
              </p>
            ) : null}
          </div>
        </div>
      </section>

      {/* ── Item 16 — Benevolence ────────────────────────────────────────── */}
      <section className="space-y-4">
        <div className="flex items-center gap-2">
          <HeartHandshake className="w-4 h-4 text-gray-700" />
          <h3 className="text-sm font-bold text-gray-900 uppercase tracking-tight">
            16 · Contributions to NIQS benevolent activities
          </h3>
        </div>
        <div className="space-y-4">
          {benevolences.map((r, i) => (
            <div key={i} className="group relative bg-white border border-gray-200 rounded-2xl p-6 hover:border-gray-300 hover:shadow-sm transition-all">
              {benevolences.length > 1 && (
                <button type="button" className="absolute top-4 right-4 p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all"
                  onClick={() => setBenevolences((x) => x.filter((_, idx) => idx !== i))}>
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
              <div className="grid md:grid-cols-2 gap-6">
                <SelectField label="Type" icon={<Tag className="w-3 h-3" />} value={r.type || ""} options={BENEVOLENCE_TYPES}
                  onChange={(v) => setBen(i, "type", v)} />
                <SelectField label="Year" icon={<Calendar className="w-3 h-3" />} value={r.year || ""} options={years}
                  onChange={(v) => setBen(i, "year", v)} />
              </div>
              <div className="mt-6 space-y-1.5">
                <label className="text-xs font-bold text-gray-700 uppercase flex items-center gap-2 tracking-tight">
                  <Type className="w-3 h-3" /> Description
                </label>
                <textarea rows={3}
                  className="w-full p-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-gray-100 focus:bg-white focus:border-gray-400 transition-all"
                  value={r.description} placeholder="Describe your contribution to the Institute"
                  onChange={(e) => setBen(i, "description", e.target.value)} />
              </div>
            </div>
          ))}
        </div>
        <button type="button" onClick={() => setBenevolences((s) => [...s, { ...emptyBen }])}
          className="flex items-center gap-2 px-5 py-2.5 text-gray-900 border-2 border-dashed border-gray-300 rounded-2xl text-xs font-bold hover:border-gray-900 transition-all">
          <PlusCircle className="w-4 h-4" /> Add Contribution
        </button>
      </section>

      {!readOnly && (
        <div className="flex justify-between items-center pt-8 border-t border-gray-100">
          <button type="button" disabled={busy} onClick={onBack}
            className="flex items-center gap-2 px-6 py-3 border border-gray-200 rounded-xl font-bold text-sm text-gray-600 hover:bg-gray-50 transition-all">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <button disabled={busy}
            className="group flex items-center gap-3 px-8 py-3 bg-gray-900 text-white rounded-xl font-bold text-sm hover:bg-gray-800 disabled:opacity-50 transition-all shadow-lg shadow-gray-200">
            {busy ? "Processing..." : "Save & Continue"}
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </button>
        </div>
      )}
    </form>
  );
}

/* ---------- Sub-components ---------- */

interface FieldProps {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  placeholder?: string;
  icon?: React.ReactNode;
}

function Field({ label, value, onChange, type = "text", placeholder, icon }: FieldProps) {
  return (
    <div className="space-y-1.5">
      <label className="text-xs font-bold text-gray-700 uppercase flex items-center gap-2 tracking-tight">
        {icon} {label}
      </label>
      <input type={type}
        className="w-full p-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-gray-100 focus:bg-white focus:border-gray-400 transition-all"
        value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}

function SelectField({
  label,
  value,
  onChange,
  options,
  icon,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: string[];
  icon?: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <label className="text-xs font-bold text-gray-700 uppercase flex items-center gap-2 tracking-tight">
        {icon} {label}
      </label>
      <select
        className="w-full p-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-gray-100 focus:bg-white focus:border-gray-400 transition-all"
        value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">Select…</option>
        {options.map((o) => (
          <option key={o} value={o}>{o}</option>
        ))}
      </select>
    </div>
  );
}
